import React from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Trash2Outline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { IconButton, Popover, Stack, Tooltip, Typography } from '@lyearn/ui';

interface DeleteSlideConfirmPopoverProps {
  disabled?: boolean;
  onConfirm: () => void;
  onOpenChange?: (open: boolean) => void;
}

export function DeleteSlideConfirmPopover({
  disabled,
  onConfirm,
  onOpenChange,
}: DeleteSlideConfirmPopoverProps) {
  const [open, setOpen] = React.useState(false);

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      setOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );
  const { t } = useTranslation('Course');

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Popover.Button>
        <Tooltip placement="top" title="Delete Slide">
          <IconButton
            appearance="secondary"
            aria-label={t('Common:Actions.Delete')}
            className={cn(
              'bg-bg-primary',
              open
                ? 'border-text-primary text-text-primary'
                : 'text-icon-error hover:bg-bg-error-light',
            )}
            disabled={disabled}
            shape="square"
            size="s"
            variant="outlined">
            <Trash2Outline
              className={cn('h-16 w-16', open ? 'text-text-primary' : 'text-icon-error')}
            />
          </IconButton>
        </Tooltip>
      </Popover.Button>
      <Popover.Content
        className="w-[16rem] rounded-8 border border-divider bg-bg-primary p-8 shadow-[0px_4px_8px_0px_rgba(0,0,0,0.04),0px_0px_2px_0px_rgba(0,0,0,0.06),0px_0px_1px_0px_rgba(0,0,0,0.04)]"
        position="right-start"
        renderAs="popover">
        <Stack direction="col" gap={2}>
          <Stack className="w-full rounded-8 bg-bg-primary px-12 py-9">
            <Typography
              className="text-12 font-semibold uppercase leading-[20px] tracking-[1.2px]"
              color="text-text-secondary">
              Delete Slide?
            </Typography>
          </Stack>
          <Stack
            className="w-full cursor-pointer rounded-8 bg-bg-error-light px-12 py-9 hover:opacity-80"
            onClick={() => {
              onConfirm();
              handleOpenChange(false);
            }}>
            <Typography className="text-14 font-normal leading-[22px]" color="text-text-error">
              Yes
            </Typography>
          </Stack>
          <Stack
            className="w-full cursor-pointer rounded-8 bg-bg-primary px-12 py-9 hover:bg-bg-secondary"
            onClick={() => handleOpenChange(false)}>
            <Typography className="text-14 font-normal leading-[22px]" color="text-text-secondary">
              No
            </Typography>
          </Stack>
        </Stack>
      </Popover.Content>
    </Popover>
  );
}
